import { default as NextLink } from 'next/link';
import { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Footer2025Sections, Maybe } from '../../types/contentstack';
import { mediaScreen } from '../../utils/mediaScreen';
import { ResponsiveContainer } from '../responsive';
import { Button } from '../ui/button';

export const Footer: FunctionComponent<IFooter> = ({
  sections,
  eyebrow,
  headline,
  subheadline,
  primary_button,
  secondary_button,
  third_button,
  marginTop = true,
}) => {
  return (
    <FullWidthDelimiter
      style={{
        marginTop: marginTop === false ? 0 : undefined,
      }}
    >
      {headline && (
        <PreFooterContainer>
          <img
            src="/img/footer-building.svg"
            width={480}
            height={480}
            alt="footer building geometry"
            aria-hidden="true"
            className="image mobile-only"
          />

          <div className="inner">
            <ResponsiveContainer>
              <div className="content">
                <div className="texts">
                  {eyebrow && <h5 className="eyebrow">{eyebrow}</h5>}
                  <h2
                    className="headline"
                    dangerouslySetInnerHTML={{ __html: headline }}
                  />
                  {subheadline && (
                    <h3
                      className="subheadline"
                      dangerouslySetInnerHTML={{ __html: subheadline }}
                    />
                  )}
                  <div className="buttons">
                    {primary_button?.title && (
                      <NextLink
                        href={primary_button?.href || ''}
                        passHref
                        legacyBehavior
                      >
                        <Button as="a" mType="primary" invert>
                          {primary_button?.title}
                        </Button>
                      </NextLink>
                    )}
                    {secondary_button?.title && (
                      <NextLink
                        href={secondary_button?.href || ''}
                        passHref
                        legacyBehavior
                      >
                        <Button as="a" mType="secondary">
                          {secondary_button?.title}
                        </Button>
                      </NextLink>
                    )}
                    {third_button?.title && (
                      <NextLink
                        href={third_button?.href || ''}
                        passHref
                        legacyBehavior
                      >
                        <Button as="a" mType="secondary">
                          {third_button?.title}
                        </Button>
                      </NextLink>
                    )}
                  </div>
                </div>
              </div>
            </ResponsiveContainer>
          </div>

          <img
            src="/img/footer-building-desktop.svg"
            alt="footer building geometry"
            aria-hidden="true"
            className="image desktop-only"
          />
        </PreFooterContainer>
      )}

      <FooterContainer
        style={
          !!headline
            ? {}
            : {
                borderTop: '1px solid #4d5f80',
              }
        }
      >
        <div className="logos">
          <img src="/img/header-logo.svg" className="mapped" />

          <div className="social">
            <a href="https://www.linkedin.com/company/mapped/" target="_blank">
              <img src="/img/icon-linkedin.svg" className="linkedin" />
            </a>
            <a href="https://twitter.com/Mapped" target="_blank">
              <img src="/img/icon-twitter.svg" className="twitter" />
            </a>
            <a href="https://github.com/mapped" target="_blank">
              <img src="/img/icon-github.svg" className="github" />
            </a>
          </div>

          <SOCLogos className="desktop-only" />
        </div>

        <div className="sections">
          {sections?.map((section, index) => (
            <div key={section?.title} className="section">
              <h4 className="title">{section?.title}</h4>

              <ul className="links">
                {section?.links?.map((link) => (
                  <li key={link?.title}>
                    <NextLink href={link?.href!}>{link?.title}</NextLink>
                  </li>
                ))}

                {section?.ref_linksConnection?.edges?.map((edge) => (
                  <li key={edge?.node?.meta?.title}>
                    <NextLink href={edge?.node?.url!}>
                      {edge?.node?.meta?.title}
                    </NextLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <SOCLogos className="mobile-only" />

        <p className="copyright">
          © Mapped {new Date().getFullYear()}. All rights reserved. SOC
          compliant.
        </p>
      </FooterContainer>
    </FullWidthDelimiter>
  );
};

const SOCLogos = ({ className }) => (
  <div className={`soc ${className}`}>
    <a
      href="https://www.prescientsecurity.com/iso-27001-certification-journey"
      target="_blank"
      title="ISO 27001 certified"
    >
      <img
        src="/img/iso-logo.png"
        width="120"
        height="120"
        alt="ISO 27001 certified"
      />
    </a>

    <a href="http://www.aicpa.org/soc4so" target="_blank" title="SOC compliant">
      <img
        src="/img/soc-logo.png"
        width="120"
        height="120"
        alt="SOC compliant"
      />
    </a>
  </div>
);

export const FOOTER_QUERY = /*gql*/ `
  footer: footer_2025(uid: "blt925cd2f25938e553") {
    sections {
      title
      links {
        title
        href
      }
      ref_linksConnection {
        edges {
          node {
            ... on Vertical {
              url
              meta {
                title
              }
            }
            ... on VerticalAlt {
              url
              meta {
                title
              }
            }
          }
        }
      }
    }
  }
`;

const FullWidthDelimiter = styled.div`
  position: relative;
  overflow: hidden;
  max-width: 1600px;
  margin: 0 auto;
  margin-top: 100px;

  > img {
    width: 100%;
  }

  .desktop-only {
    display: flex;
  }

  .mobile-only {
    display: none;
  }

  ${mediaScreen('xs', 'md')} {
    margin-top: 60px;

    .desktop-only {
      display: none;
    }

    .mobile-only {
      display: block;
    }
  }
`;

const PreFooterContainer = styled.section`
  width: 100%;
  position: relative;
  background: linear-gradient(116deg, #16806b 0%, #2aa38d 40%, #51c0ac 100%);
  overflow: hidden;
  color: white;

  .inner {
    position: relative;
    width: 100%;
    padding: 80px 0;
  }

  .content {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    .texts {
      max-width: 672px;

      .eyebrow {
        display: inline-block;
        letter-spacing: 4px;
        font-size: 16px;
        font-weight: 700;
        color: rgba(255, 255, 255, 0.9);
        margin: 0 0 24px;
        text-transform: uppercase;
      }

      .headline {
        margin: 0;
        font-size: 50px;
        line-height: 1.15;
        font-weight: 700;
        letter-spacing: -0.5px;
        color: white;
      }

      .subheadline {
        margin-top: 28px;
        font-size: 18px;
        line-height: 32px;
        color: rgba(255, 255, 255, 0.9);
        font-weight: 400;
      }

      .buttons {
        display: flex;
        gap: 24px;
        flex-wrap: wrap;
        align-items: center;
        margin-top: 40px;
        a {
          text-decoration: none;
        }
      }
    }
  }

  .image {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 55%;
    min-width: 500px;
    pointer-events: none;
    opacity: 0.55;
  }

  ${mediaScreen('xs', 'md')} {
    .inner {
      padding: 44px 0;
      padding-top: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .content {
      flex-direction: column;
      align-items: center;
      text-align: center;

      .texts {
        max-width: 100%;

        .headline {
          font-size: 30px;
          line-height: 1.18;
        }

        .subheadline {
          font-size: 16px;
          line-height: 22px;
          margin-top: 20px;
        }

        .buttons {
          flex-direction: column;
          gap: 16px;
          width: 100%;
          button,
          a {
            width: 100%;
            text-align: center;
          }
        }
      }
    }

    .image {
      position: static;
      transform: none;
      width: 90%;
      min-width: unset;
      margin: 0 auto;
      margin-top: 40px;
      display: block;
      margin-bottom: -30%;

      &.desktop-only {
        display: none;
      }
    }
  }
`;

const FooterContainer = styled(ResponsiveContainer)`
  padding-top: 60px;
  padding-bottom: 60px;

  .logos {
    display: flex;
    align-items: center;
    position: relative;

    .mapped {
      width: 180px;
      margin-right: 40px;
    }

    .social {
      display: flex;
      align-items: center;
      position: relative;
      top: -3px;
      gap: 24px;

      a {
        &:hover {
          opacity: 0.5;
        }
      }

      img {
        height: 22px;
      }

      .twitter {
        height: 20px;
        position: relative;
        top: 1.5px;
      }
    }

    .soc {
      position: absolute;
      right: 0;
      gap: 32px;

      a {
        :hover {
          opacity: 0.5;
        }
      }
      img {
        height: 90px;
        width: auto;
      }
    }
  }

  .sections {
    margin-top: 64px;
    display: flex;
    flex-wrap: nowrap;
    gap: 32px;

    .section {
      flex: 1;

      .title {
        text-transform: uppercase;
        color: ${(props) => props.theme.colors.slate3};
        font-size: 16px;
        font-weight: 700;
        line-height: 1.5;
        letter-spacing: 3px;
        margin: 0;
        margin-bottom: 10px;
      }

      .links {
        padding: 0;
        margin: 0;
        list-style: none;

        li {
          margin-bottom: 10px;

          a {
            color: white;
            text-decoration: none;
            font-size: 16px;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }
  }

  .copyright {
    padding: 0;
    margin: 0;
    margin-top: 44px;
    padding-top: 32px;
    font-size: 15px;
    color: ${(props) => props.theme.colors.slate3};
    border-top: 1px solid ${(props) => props.theme.colors.slate4};
  }

  ${mediaScreen('xs', 'md')} {
    padding-top: 40px;
    padding-bottom: 40px;

    .logos {
      justify-content: space-between;

      .mapped {
        margin-right: 0;
      }
    }

    .sections {
      margin-top: 40px;
      gap: 24px;
      flex-direction: column;

      .section {
        .title {
          padding: 12px 0;
          margin-bottom: 16px;
          border-bottom: 1px solid ${(props) => props.theme.colors.slate4};
        }
      }
    }

    .soc.mobile-only {
      display: flex;
      gap: 44px;
      margin-top: 44px;

      img {
        width: auto;
        height: 120px;
      }
    }
  }
`;

interface IFooter {
  sections?: Maybe<Maybe<Footer2025Sections>[]>;
  eyebrow?: string | null;
  headline?: string | null;
  subheadline?: string | null;
  primary_button?: {
    title?: string | null;
    href?: string | null;
  } | null;
  secondary_button?: {
    title?: string | null;
    href?: string | null;
  } | null;
  third_button?: {
    title?: string | null;
    href?: string | null;
  } | null;
  marginTop?: boolean;
}
